/**
 * OrgShowcasePanel — header + 4/4 split (donor wall + top campaign) +
 * YTD aggregate strip.
 *
 * Wall paginates every rotation_interval_seconds via FeedRotator with a
 * CSS crossfade between pages. Pill-shaped page dots mirror the Flutter
 * app. Port of apps/signage-tv/lib/rendering/org_showcase_panel.dart.
 *
 * Spec: signage-device-app-spec §3.3
 */

import { applyBranding, Branding, extractBranding, formatCurrency } from './branding.js';
import { FeedRotator } from './feed-rotator.js';
import { renderAggregateTile } from '../primitives/aggregate-tile.js';
import { renderDonorTile } from '../primitives/donor-tile.js';
import { renderThermometer } from '../primitives/thermometer.js';

const WALL_PAGE_SIZE = 6;
const MIN_ROTATION_SECONDS = 5;
const MAX_ROTATION_SECONDS = 120;
const FADE_MS = 450;

export interface OrgShowcaseState {
  config: Record<string, unknown> | null;
  orgContent: Record<string, unknown> | null;
}

export class OrgShowcaseRenderer {
  private readonly rotator = new FeedRotator(WALL_PAGE_SIZE);
  private timer: ReturnType<typeof setInterval> | null = null;
  private lastEntries: ReadonlyArray<Record<string, unknown>> = [];
  private lastIntervalSeconds = 15;
  private branding: Branding;

  constructor(private readonly root: HTMLElement) {
    this.branding = extractBranding(null);
  }

  dispose(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
  }

  render(state: OrgShowcaseState): void {
    this.branding = extractBranding(state.config);
    applyBranding(this.root, this.branding);

    const oc = state.orgContent ?? {};
    const wall = Array.isArray(oc['donor_wall'])
      ? (oc['donor_wall'] as ReadonlyArray<Record<string, unknown>>)
      : [];
    const topCampaign = (oc['top_campaign'] as Record<string, unknown> | undefined) ?? null;
    const aggregate = (oc['aggregate'] as Record<string, unknown> | undefined) ?? null;

    this.lastEntries = wall;
    // Reset cursor if the source shrunk beneath our position.
    if (this.rotator.page >= this.rotator.pageCount(wall.length)) {
      this.rotator.reset();
    }

    const rotationSeconds = clampRotation(
      (state.config?.['rotation_interval_seconds'] as number | undefined) ?? 15
    );
    if (rotationSeconds !== this.lastIntervalSeconds || this.timer === null) {
      this.lastIntervalSeconds = rotationSeconds;
      this.scheduleTimer();
    }

    this.paint(wall, topCampaign, aggregate);
  }

  /* ---------------- timer ---------------- */

  private scheduleTimer(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = setInterval(() => this.tick(), this.lastIntervalSeconds * 1000);
  }

  private tick(): void {
    const moved = this.rotator.advance(this.lastEntries.length);
    if (!moved) return;
    // Crossfade: add --exiting for FADE_MS, then repaint the wall.
    const page = this.root.querySelector<HTMLElement>('.ag-wall__page');
    if (page) {
      page.classList.add('ag-wall__page--exiting');
      setTimeout(() => this.paintWallOnly(), FADE_MS);
    } else {
      this.paintWallOnly();
    }
  }

  /* ---------------- paint ---------------- */

  private paint(
    wall: ReadonlyArray<Record<string, unknown>>,
    top: Record<string, unknown> | null,
    aggregate: Record<string, unknown> | null
  ): void {
    this.root.innerHTML = '';
    this.root.className = 'ag-app ag-showcase';

    const header = document.createElement('div');
    header.className = 'ag-showcase__header';
    header.textContent = this.branding.orgName;
    this.root.appendChild(header);

    const body = document.createElement('div');
    body.className = 'ag-showcase__body';
    this.root.appendChild(body);

    const wallBox = document.createElement('div');
    wallBox.className = 'ag-wall';
    body.appendChild(wallBox);
    this.paintWallInto(wallBox, wall);

    const divider = document.createElement('div');
    divider.className = 'ag-showcase__divider';
    body.appendChild(divider);

    const campaignBox = document.createElement('div');
    campaignBox.className = 'ag-campaign';
    body.appendChild(campaignBox);
    this.paintTopCampaign(campaignBox, top);

    const agg = document.createElement('div');
    agg.className = 'ag-showcase__aggregate';
    this.paintAggregate(agg, aggregate);
    this.root.appendChild(agg);
  }

  private paintWallOnly(): void {
    const wallBox = this.root.querySelector<HTMLElement>('.ag-wall');
    if (!wallBox) return;
    wallBox.innerHTML = '';
    this.paintWallInto(wallBox, this.lastEntries);
  }

  private paintWallInto(
    wallBox: HTMLElement,
    entries: ReadonlyArray<Record<string, unknown>>
  ): void {
    if (entries.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'ag-wall__empty';
      empty.textContent = 'Recent donors will appear here';
      wallBox.appendChild(empty);
      return;
    }
    const page = document.createElement('div');
    page.className = 'ag-wall__page';
    const visible = this.rotator.currentPage(entries);
    for (const e of visible) {
      const cents = typeof e['amount_minor_units'] === 'number' ? (e['amount_minor_units'] as number) : 0;
      page.appendChild(
        renderDonorTile({
          donorDisplay:
            typeof e['donor_display'] === 'string' ? (e['donor_display'] as string) : 'Donor',
          amountDisplay: formatCurrency(cents, this.branding),
          designation:
            typeof e['designation_name'] === 'string' ? (e['designation_name'] as string) : null,
          // CHG-0083-E — backend feed/wall now includes source per row
          source: typeof e['source'] === 'string' ? (e['source'] as string) : null,
        })
      );
    }
    wallBox.appendChild(page);

    const totalPages = this.rotator.pageCount(entries.length);
    if (totalPages > 1) {
      const dots = document.createElement('div');
      dots.className = 'ag-wall__dots';
      for (let i = 0; i < totalPages; i++) {
        const dot = document.createElement('div');
        dot.className = 'ag-wall__dot';
        if (i === this.rotator.page) dot.classList.add('ag-wall__dot--active');
        dots.appendChild(dot);
      }
      wallBox.appendChild(dots);
    }
  }

  private paintTopCampaign(box: HTMLElement, campaign: Record<string, unknown> | null): void {
    if (!campaign) {
      const empty = document.createElement('div');
      empty.className = 'ag-wall__empty';
      empty.textContent = 'No active campaigns';
      box.appendChild(empty);
      return;
    }
    const kicker = document.createElement('div');
    kicker.className = 'ag-campaign__kicker';
    kicker.textContent = 'Top Campaign';
    box.appendChild(kicker);

    const name = document.createElement('h2');
    name.className = 'ag-campaign__name';
    name.textContent = typeof campaign['name'] === 'string' ? (campaign['name'] as string) : '';
    box.appendChild(name);

    const raised = typeof campaign['raised_minor_units'] === 'number' ? (campaign['raised_minor_units'] as number) : 0;
    const goal = typeof campaign['goal_minor_units'] === 'number' ? (campaign['goal_minor_units'] as number) : 0;
    const pct = goal > 0 ? Math.max(0, Math.min(1, raised / goal)) : 0;

    box.appendChild(renderThermometer({ percent: pct, size: 'md' }));

    const totals = document.createElement('div');
    totals.className = 'ag-campaign__totals';
    totals.textContent = `${formatCurrency(raised, this.branding)} of ${formatCurrency(
      goal,
      this.branding
    )}`;
    box.appendChild(totals);
  }

  private paintAggregate(container: HTMLElement, agg: Record<string, unknown> | null): void {
    const ytd = typeof agg?.['total_raised_ytd_minor_units'] === 'number' ? (agg['total_raised_ytd_minor_units'] as number) : 0;
    const donors = typeof agg?.['donor_count'] === 'number' ? (agg['donor_count'] as number) : 0;
    const active = typeof agg?.['active_campaigns'] === 'number' ? (agg['active_campaigns'] as number) : 0;
    container.appendChild(
      renderAggregateTile({ label: 'Raised YTD', value: formatCurrency(ytd, this.branding) })
    );
    container.appendChild(renderAggregateTile({ label: 'Donors', value: String(donors) }));
    container.appendChild(
      renderAggregateTile({ label: 'Active Campaigns', value: String(active) })
    );
  }
}

function clampRotation(raw: number): number {
  if (!Number.isFinite(raw)) return 15;
  return Math.max(MIN_ROTATION_SECONDS, Math.min(MAX_ROTATION_SECONDS, Math.floor(raw)));
}
